import type { RcFile } from 'antd/lib/upload';
import { err, ok, type Result } from '@/std/result';
import { uuid } from '@/std/util';

/** Codifica uma string para base64 */
export function strToBase64(value: string): string {
    return window.btoa(value);
}

/** Decodifica um valor em base64 para string */
// https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_.22unicode_problem.22
export function base64ToStr(value: string): string {
    const bytes = window.atob(value);
    const arr = Uint8Array.from(bytes, (m) => m.codePointAt(0) || 0);
    return new TextDecoder().decode(arr);
}

// https://stackoverflow.com/questions/36280818/how-to-convert-file-to-base64-in-javascript
/**
 * Converte um arquivo em base64
 * @param file - O arquivo a ser convertido
 * @param hideData - Esconde o inicio do base64 que começa com "data"
 * */
export function fileToBase64(file: Blob | RcFile, hideData = true): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => {
            if (reader.result) {
                let encoded = typeof reader.result === 'string' ? reader.result : '';

                if (hideData) {
                    encoded = encoded.replace(/^data:(.*,)?/, '');

                    if (encoded.length % 4 > 0) {
                        encoded += '='.repeat(4 - (encoded.length % 4));
                    }
                }

                resolve(encoded);
            }
        };

        reader.onerror = (error) => reject(error);
    });
}

/** Decodifica um valor em base64 para um PDF no formato Blob */
export function base64ToPDF(base64: string): Blob {
    const byteCharacters = window.atob(base64);
    const byteNumbers: number[] = Array.from({ length: byteCharacters.length });

    for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'application/pdf;base64' });
}

export function base64ToFile(base64: string, fileName: string, mimeType: string): Result<File> {
    const msg = 'Erro ao converter para arquivo';

    try {
        let src = '';

        if (base64.startsWith('data:')) {
            src = base64.split(',')[1] || '';
        } else {
            src = base64;
        }

        const byteCharacters = atob(src);
        const byteNumbers = Array.from({ length: byteCharacters.length }).map((_, i) =>
            byteCharacters.charCodeAt(i),
        );
        const byteArray = new Uint8Array(byteNumbers);
        const file = new File([byteArray], fileName, { type: mimeType });
        return ok(file);
    } catch {
        return err(msg);
    }
}

/** Codifica um arquivo vindo de uma url para base64 */
export function urlToBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.onload = () => {
            const reader = new FileReader();

            reader.onloadend = () => {
                const str = typeof reader.result === 'string' ? reader.result : '';
                const result = str.replace('data:binary/octet-stream;base64,', '');
                resolve(result);
            };

            reader.readAsDataURL(xhr.response);
        };

        xhr.onerror = (error) => reject(error);

        xhr.open('GET', `${url}?${uuid()}`);
        xhr.responseType = 'blob';
        xhr.send();
    });
}

export function downloadBase64File(base64: string, fileName: string, mimeType: string): void {
    // Decode the Base64 string
    const byteCharacters = atob(base64);

    // Create an array for the byte data
    const byteNumbers: number[] = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // Create a Blob from the byteArray
    const blob = new Blob([byteArray], { type: mimeType });

    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    // Trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
}

export async function awsImageToBase64(url: string): Promise<string> {
    if (!url) {
        return '';
    }

    // const buildNoCacheUrl = (url: string): string => {
    //     try {
    //         const parsedUrl = new URL(url, window.location.href);
    //         parsedUrl.searchParams.set('cb', String(Date.now()));
    //         return parsedUrl.toString();
    //     } catch {
    //         const separator = url.includes('?') ? '&' : '?';
    //         return `${url}${separator}cb=${Date.now()}`;
    //     }
    // };

    // const noCacheUrl = buildNoCacheUrl(url);

    try {
        const response = await fetch(url, {
            cache: 'no-store',
            credentials: 'omit',
            method: 'GET',
            mode: 'cors',
        });

        if (response.ok) {
            const blob = await response.blob();

            return await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                    resolve(typeof reader.result === 'string' ? reader.result : '');
                };

                reader.onerror = () => {
                    reject(new Error('Erro ao converter imagem para base64'));
                };

                reader.readAsDataURL(blob);
            });
        }
    } catch {
        // Fallback to Image+canvas below.
    }

    return await new Promise<string>((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';

        image.onload = () => {
            const canvas = document.createElement('canvas');
            const width = image.naturalWidth || image.width;
            const height = image.naturalHeight || image.height;

            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d');

            if (!context) {
                resolve('');
                return;
            }

            context.drawImage(image, 0, 0);

            try {
                resolve(canvas.toDataURL('image/png'));
            } catch (error) {
                console.error('Erro ao converter imagem para base64 via canvas:', error);
                reject(new Error('Erro ao converter imagem para base64'));
            }
        };

        image.onerror = () => {
            reject(new Error('Erro ao carregar imagem'));
        };

        image.src = url;
    });
}
